import { useFormContext, useWatch } from 'react-hook-form';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Banner, Button, IconName, Stack, TextInput } from '@optimus-web/core';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { ConfigureScimFieldNames, ConfigureScimFormData } from '../useConfigureScimForm';
import { CopyableInput } from './CopyableInput';

const OBFUSCATED_TOKEN = '************************';

const getSecretTokenFieldProps = (value: string) => ({
    name: 'secret-token',
    label: <LocalizedText textKey={TextKey.SecretToken} />,
    readOnly: true,
    size: 'medium' as const,
    value,
});

export const ScimSecretTokenField = () => {
    const { setValue } = useFormContext<ConfigureScimFormData>();
    const { mutate, isPending, data } = useRepositoryMutation('scimConfiguration', 'issueToken', {
        onSuccess: (tokenData) => {
            setValue(ConfigureScimFieldNames.Enabled, true, { shouldDirty: true, shouldValidate: true });
            setValue(ConfigureScimFieldNames.SecretToken, tokenData.token, { shouldDirty: true, shouldValidate: true });
        },
    });
    const generateToken = () => mutate({});

    const hasStoredToken = useWatch<ConfigureScimFormData>({
        name: ConfigureScimFieldNames.HasPreviousToken,
    });

    if (data?.token) {
        return (
            <Stack vertical spacing="100" fill>
                <Banner
                    variant="warning"
                    primaryText={<LocalizedText textKey={TextKey.ScimCopySecretToken} />}
                    secondaryText={<LocalizedText textKey={TextKey.ScimCopySecretTokenDescription} />}
                />

                <CopyableInput {...getSecretTokenFieldProps(data.token)} />
            </Stack>
        );
    }

    if (!hasStoredToken) {
        return (
            <Button
                variant="secondary"
                size="medium"
                type="button"
                icon={IconName.Key}
                iconPosition="left"
                onClick={generateToken}
                loading={isPending}
            >
                <LocalizedText textKey={TextKey.GenerateSecretToken} />
            </Button>
        );
    }

    return (
        <Stack fill spacing="100" itemAlignment="end">
            <Stack fill distribution="stretch">
                <TextInput {...getSecretTokenFieldProps(OBFUSCATED_TOKEN)} />
            </Stack>
            <Button
                size="medium"
                type="button"
                loading={isPending}
                onClick={generateToken}
                icon={IconName.Refresh}
                iconPosition="left"
            >
                <LocalizedText textKey={TextKey.Regenerate} />
            </Button>
        </Stack>
    );
};
